import React from "react"

import { PortableText } from "@portabletext/react"
import imageUrlBuilder from "@sanity/image-url"
import PropTypes from "prop-types"
import {
  InstagramEmbed,
  TikTokEmbed,
  TwitterEmbed,
  YouTubeEmbed,
} from "react-social-media-embed"

import Button from "../button"
import * as styles from "./copy-container.module.scss"
import DarkColor from "../../../helpers/dark-color"

const components = (full) => {
  const builder = imageUrlBuilder({
    projectId: process.env.GATSBY_SANITY_PROJECT_ID,
    dataset: process.env.GATSBY_SANITY_DATASET,
    useCdn: false,
  })

  const urlFor = (source) => builder.image(source)

  const imageMarkup = (value, isFeaturedImage) => (
    <figure>
      <img
        className={full ? "" : styles.image}
        alt={isFeaturedImage ? value.alt : ""}
        src={urlFor(isFeaturedImage ? value.image : value)
          .width(1280)
          .dpr(2)
          .format("webp")
          .url()}
      />
      {isFeaturedImage ? <figcaption>{value.caption}</figcaption> : ""}
    </figure>
  )

  return {
    types: {
      image: ({ value }) => imageMarkup(value),
      article_featured_image: ({ value }) =>
        value.url ? (
          <a
            className={styles.imageLink}
            href={value.url}
            target="_blank"
            rel="noreferrer"
          >
            {imageMarkup(value, true)}
          </a>
        ) : (
          <>{imageMarkup(value, true)}</>
        ),
      instagram_post: ({ value }) => (
        <div className={styles.social}>
          <InstagramEmbed url={value.url} />
        </div>
      ),
      twitter_post: ({ value }) => (
        <div className={styles.social}>
          <TwitterEmbed url={value.url} />
        </div>
      ),
      tik_tok_post: ({ value }) => (
        <div className={styles.social}>
          <TikTokEmbed url={value.url} />
        </div>
      ),
      youtube_post: ({ value }) => (
        <div className={styles.social}>
          <YouTubeEmbed width="100%" url={value.url} />
        </div>
      ),
      cta: ({ value }) => (
        <div className={styles.button}>
          <Button fullWidth url={value.url} variant={value.variant}>
            {value.text}
          </Button>
        </div>
      ),
    },
    block: {
      small: ({ children }) => <span className={styles.small}>{children}</span>,
    },
    marks: {
      link: ({ children, value }) => (
        <a href={value.href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      ),
    },
  }
}

const colorClass = (bg, light) => {
  if (DarkColor(bg) || light) return styles.copyContainer__light
  if (bg && bg !== "white" && bg !== "paper") return styles.copyContainer__color
  return null
}

const CopyContainer = ({
  className,
  customClass,
  content,
  children,
  bg,
  light,
  alignment,
  full,
  ads,
}) => (
  <div
    className={`${className} ${styles[`${customClass}`]} ${styles.copyContainer} ${colorClass(
      bg,
      light
    )} ${alignment ? styles[`copyContainer__${alignment}`] : ""}`}
  >
    {children ||
      content?.map((section, index) => (
        <>
          <PortableText value={section} components={components(full)} />
          {index % 4 === 0 && ads ? (
            <div className={`lngtd-dynamic-inarticle ${styles.ad}`} />
          ) : (
            ""
          )}
        </>
      ))}
  </div>
)

export default CopyContainer

CopyContainer.propTypes = {
  className: PropTypes.string,
  customClass: PropTypes.string,
  content: PropTypes.array,
  children: PropTypes.node,
  bg: PropTypes.string,
  alignment: PropTypes.string,
  full: PropTypes.bool,
  ads: PropTypes.bool,
  light: PropTypes.bool,
}

CopyContainer.defaultProps = {
  className: "",
  customClass: "",
  content: [],
  children: null,
  bg: "",
  alignment: null,
  full: false,
  ads: false,
  light: false,
}
